import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import UpRightArrow from '@mui/icons-material/NorthEast';
import Schedule from '@mui/icons-material/Schedule';
import Send from '@mui/icons-material/Send';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Modal from '@mui/material/Modal';
import Popover from '@mui/material/Popover';
import Select from '@mui/material/Select';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { PrimaryBtn } from 'components/UI/Buttons';
import useIsMobile from 'hooks/useIsMobile';
import QuickReplies from 'pages/Client/QuickReplies';
import React, { type ClipboardEvent, type FC, useCallback, useEffect, useState } from 'react';
import { usePostFileMessageMutation, usePostMessageMutation, useTypingMutation } from 'services/api/messages';
import { usePostScheduledMessageMutation } from 'services/api/scheduledMessages.api';
import type { Client } from 'services/types/client';
import type { Message, MessageStatus } from 'services/types/message';
import type { QuickReplyFile } from 'services/types/quickReply';
import type { ScheduledMessage } from 'services/types/scheduledMessage';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  SHADOW,
  SHADOW_CARD,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';

import EmailComposer from './EmailComposer';
import VoiceRecorder from './VoiceRecorder';

// Common emojis matrix
const COMMON_EMOJIS = [
  ['😊', '👍', '🎉', '❤️', '🙏', '✨', '💪', '🤝'],
  ['👋', '🌟', '💯', '🔥', '👏', '🎯', '✅', '🚗'],
  ['📱', '💼', '📊', '📈', '🎁', '🛒', '💰', '📝'],
  ['🔔', '⏰', '📅', '🌈', '🚀', '🏆', '💡', '🔍'],
];

interface Props {
  client: Client;
  conversation: Message[] | undefined;
  setConversation: React.Dispatch<React.SetStateAction<Message[]>>;
  addScheduledMessage: (message: ScheduledMessage) => void;
  isConversationActive: boolean;
  newMessagePrefix?: string | null;
  onOpenTemplateModal?: () => void;
}

type Hour = {
  value: number;
  label: string;
};

const ChatFooter: FC<Props> = ({
  client,
  conversation,
  setConversation,
  addScheduledMessage,
  isConversationActive,
  newMessagePrefix,
  onOpenTemplateModal,
}) => {
  const [messageToSend, setMessage] = useState('');
  const [fileToSend, setFileToSend] = useState<File | null>(null);
  // Quick reply file: already in the bucket, sent by path (browser can't fetch GCS urls — no CORS).
  const [bucketFileToSend, setBucketFileToSend] = useState<QuickReplyFile | null>(null);
  const [filePreview, setFilePreview] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [openScheduleModal, setOpenScheduleModal] = useState(false);
  const [selectedHour, setSelectedHour] = useState<number | ''>('');
  const [selectedMinute, setSelectedMinute] = useState<number | ''>('');
  const [availableHours, setAvailableHours] = useState<Hour[]>([]);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isRecordingAudio, setIsRecordingAudio] = useState(false);

  // Typing state
  const [lastTypingEventTime, setLastTypingEventTime] = useState<number | null>(null);

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const messageInputRef = React.useRef<HTMLTextAreaElement>(null);
  const footerRef = React.useRef<HTMLDivElement>(null);

  const clearFileInput = () => {
    setFileToSend(null);
    setBucketFileToSend(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const [postMessageMutation, { data: newMessage, isLoading: isSendingMessage }] = usePostMessageMutation();
  const [postFileMutation, { data: newFileMessage, isLoading: isSendingFile }] = usePostFileMessageMutation();
  const [postScheduledMessageMutation, { isLoading: isSchedulingMessage }] = usePostScheduledMessageMutation();
  const [typingMutation] = useTypingMutation();
  const { isMobile } = useIsMobile();

  const updateAvailableHours = useCallback(() => {
    const lastUserMessage = conversation
      ?.filter((msg) => msg.role === 'user')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

    if (!lastUserMessage) {
      setScheduleError('No hay mensajes previos del usuario, debes usar una plantilla para comenzar una conversación');
      return;
    }

    const now = new Date();
    const currentHour = now.getHours();
    const lastMessageTime = new Date(lastUserMessage.createdAt);
    const maxTime = new Date(lastMessageTime.getTime() + 23 * 60 * 60 * 1000);
    const maxHour = maxTime.getHours();

    const hoursMaxLimit = (maxTime.getTime() - now.getTime()) / (1000 * 60 * 60);
    const hours: Hour[] = [];
    let hour = currentHour;

    while (hour <= 23 && hours.length < hoursMaxLimit) {
      hours.push({ value: hour, label: hours.length === 0 ? `Hoy: ${hour} hrs` : `${hour} hrs` });
      hour += 1;
    }
    hour = 0;
    while (hour <= maxHour && hours.length < hoursMaxLimit) {
      hours.push({ value: hour, label: hour === 0 ? `Mañana: ${hour} hrs` : `${hour} hrs` });
      hour += 1;
    }

    setAvailableHours(hours);
    setSelectedHour(hours[1]?.value ?? '');
    setSelectedMinute(0);
  }, [conversation]);

  useEffect(() => {
    if (openScheduleModal) {
      updateAvailableHours();
    }
  }, [openScheduleModal, updateAvailableHours]);

  const handleScheduleMessage = () => {
    if (messageToSend.length === 0 || selectedHour === '' || selectedMinute === '') {
      return;
    }

    const scheduledTime = new Date();
    scheduledTime.setHours(selectedHour, selectedMinute, 0, 0);

    postScheduledMessageMutation({
      clientId: client.id,
      text: messageToSend,
      postAt: scheduledTime.toISOString(),
    })
      .then((response) => {
        if ('data' in response && response.data) {
          addScheduledMessage(response.data);
          setMessage('');
          setOpenScheduleModal(false);
        }
        if ('error' in response && response.error) {
          setScheduleError(
            `Error al programar el mensaje. ${'data' in response.error ? String(response.error.data) : ''}`
          );
          console.error('Error al programar el mensaje', response.error);
        }
      })
      .catch((err) => {
        setScheduleError('Error al programar el mensaje');
        console.error('Error al programar el mensaje', err);
      });
  };

  const handleEmojiClick = (emoji: string) => {
    const textarea = messageInputRef.current;
    const start = textarea?.selectionStart ?? messageToSend.length;
    const end = textarea?.selectionEnd ?? messageToSend.length;
    const newText = messageToSend.substring(0, start) + emoji + messageToSend.substring(end);

    setMessage(newText);
    handleTyping(newText);
    setEmojiAnchorEl(null);

    // Restore focus and place cursor right after the inserted emoji
    setTimeout(() => {
      textarea?.focus();
      const newPosition = start + emoji.length; // just after the emoji
      textarea?.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const handleSendMessage = () => {
    if (messageToSend.length === 0) {
      return;
    }
    const text = `${newMessagePrefix || ''}${messageToSend}`;
    postMessageMutation({ clientId: client.id, text }).catch((err) => console.error(err));
    setMessage('');
    setLastTypingEventTime(null);
  };

  /**
   * Handles typing events with throttling to prevent excessive API calls.
   * Only sends a typing event if more than 25 seconds have passed since the last one.
   *
   * @param {string} value - The current value of the input field
   */
  const handleTyping = (value: string) => {
    const now = Date.now();
    const shouldSendTypingEvent = !lastTypingEventTime || now - lastTypingEventTime > 25000;

    if (value.length > 0 && shouldSendTypingEvent) {
      setLastTypingEventTime(now);
      typingMutation({ clientId: client.id }).catch((err) =>
        console.error('[ChatFooter][handleTyping] Error sending typing event', err)
      );
    }
  };

  useEffect(() => {
    if (newMessage) {
      const newMessageWithSentStatus = { ...newMessage, status: 'sent' as MessageStatus };
      setConversation((prev) => [...(prev ?? []), newMessageWithSentStatus]);
    }
  }, [newMessage, setConversation]);

  useEffect(() => {
    if (newFileMessage) {
      setConversation((prev) => [...(prev ?? []), { ...newFileMessage, status: 'sent' as MessageStatus }]);
    }
  }, [newFileMessage, setConversation]);

  useEffect(() => {
    if (bucketFileToSend?.url) {
      setOpenModal(true);
      setFilePreview(bucketFileToSend.url);
    }
  }, [bucketFileToSend]);

  useEffect(() => {
    if (fileToSend) {
      setOpenModal(true);
      const isVideo = fileToSend.name.endsWith('.mp4');
      const isPDF = fileToSend.name.endsWith('.pdf');
      const IMAGE_MB_LIMIT = 5;
      const VIDEO_MB_LIMIT = 16;
      const DOC_MB_LIMIT = 100;
      let sizeLimit = IMAGE_MB_LIMIT;
      if (isVideo) sizeLimit = VIDEO_MB_LIMIT;
      if (isPDF) sizeLimit = DOC_MB_LIMIT;

      if (fileToSend.size > sizeLimit * 1024 * 1024) {
        setError(`El archivo no puede pesar más de ${sizeLimit} mb`);
        return () => {};
      }
      const objectUrl = URL.createObjectURL(fileToSend);
      setFilePreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
    return () => {};
  }, [fileToSend]);

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const items = e.clipboardData?.items;

    if (!items || items.length === 0) return;

    const file = items[0].getAsFile();
    if (file) {
      setFileToSend(file);
      e.preventDefault();
    }
  };

  if (client.chatType === 'email') {
    return <EmailComposer client={client} setConversation={setConversation} />;
  }

  if (!isConversationActive) {
    const isEmpty = conversation?.length === 0;
    return (
      <Box
        sx={{
          mx: 2,
          mb: 2,
          mt: 1,
          px: 3,
          py: 2.5,
          backgroundColor: GLASS_BACKGROUND,
          backdropFilter: GLASS_BLUR,
          borderRadius: RADIUS_CARD,
          boxShadow: SHADOW_CARD,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: alpha(NAVY, 0.75),
            fontWeight: 500,
            textAlign: { xs: 'center', sm: 'left' },
          }}
        >
          {isEmpty
            ? 'No hay mensajes de este cliente'
            : 'Han pasado más de 24 horas desde el último contacto del cliente'}
        </Typography>
        {onOpenTemplateModal ? (
          <Button
            onClick={onOpenTemplateModal}
            disabled={client.chatType === 'pusher'}
            sx={{
              backgroundColor: YELLOW,
              color: NAVY,
              borderRadius: RADIUS_COIN,
              fontWeight: 600,
              textTransform: 'none',
              px: 2.5,
              py: 0.75,
              boxShadow: 'none',
              flexShrink: 0,
              '&:hover': {
                backgroundColor: YELLOW,
                opacity: 0.9,
                boxShadow: SHADOW,
              },
              '&.Mui-disabled': {
                backgroundColor: YELLOW,
                color: NAVY,
                opacity: 0.4,
              },
            }}
          >
            {isEmpty ? 'Iniciar conversación' : 'Retomar conversación'}
          </Button>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: alpha(NAVY, 0.6) }}>
            <Typography variant="body2" sx={{ color: alpha(NAVY, 0.6) }}>
              Puedes enviar una plantilla usando las opciones del chat
            </Typography>
            <UpRightArrow fontSize="small" />
          </Box>
        )}
      </Box>
    );
  }

  const placeholderMessage = isMobile
    ? 'Escribe aquí...'
    : 'Escribe aquí... (también puedes pegar un archivo o screenshot)';

  // The send-preview modal handles both a local file and a bucket-stored quick reply file.
  const sendFileName = fileToSend?.name ?? bucketFileToSend?.fileName ?? '';
  const handleSendFile = () => {
    if (fileToSend) {
      postFileMutation({ clientId: client.id, file: fileToSend }).catch((err) => console.error(err));
    } else if (bucketFileToSend) {
      postFileMutation({ clientId: client.id, path: bucketFileToSend.path, fileName: bucketFileToSend.fileName }).catch(
        (err) => console.error(err)
      );
    }
    clearFileInput();
    setOpenModal(false);
  };

  return (
    <>
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90vw', sm: '80vw', md: '70vw', lg: '60vw' },
            maxWidth: '900px',
            maxHeight: '90vh',
            backdropFilter: GLASS_BLUR,
            backgroundColor: GLASS_BACKGROUND,
            borderRadius: RADIUS_CARD,
            boxShadow: SHADOW,
            border: `1px solid ${alpha(NAVY, 0.06)}`,
            p: 4,
            overflow: 'auto',
          }}
        >
          {error && (
            <>
              <Typography variant="h5" color="error">
                {error}
              </Typography>
              <PrimaryBtn
                variant="contained"
                color="error"
                onClick={() => {
                  clearFileInput();
                  setOpenModal(false);
                  setError(null);
                }}
                sx={{ mr: 2 }}
              >
                Cerrar
              </PrimaryBtn>
            </>
          )}
          {!error && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  mb: 2,
                }}
              >
                {sendFileName.endsWith('.pdf') ? 'Archivo' : sendFileName.endsWith('.mp4') ? 'Video' : 'Imagen'} a
                enviar: {sendFileName}
              </Typography>
              {filePreview && (
                <Box
                  sx={{
                    width: '100%',
                    maxHeight: 'calc(90vh - 200px)',
                    overflow: 'auto',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: RADIUS_CARD,
                    backgroundColor: SURFACE_CONTAINER_LOW,
                  }}
                >
                  {sendFileName.endsWith('.pdf') && (
                    <embed src={filePreview} width="100%" height="calc(90vh - 200px)" style={{ minHeight: '400px' }} />
                  )}
                  {sendFileName.endsWith('.mp4') && (
                    <video src={filePreview} controls style={{ maxWidth: '100%', maxHeight: 'calc(90vh - 200px)' }}>
                      <track kind="captions" />
                    </video>
                  )}
                  {(sendFileName.endsWith('.png') ||
                    sendFileName.endsWith('.jpg') ||
                    sendFileName.endsWith('.jpeg')) && (
                    <img
                      src={filePreview}
                      alt={sendFileName || 'file to send'}
                      style={{
                        maxWidth: '100%',
                        maxHeight: 'calc(90vh - 200px)',
                        height: 'auto',
                        objectFit: 'contain',
                      }}
                    />
                  )}
                </Box>
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <PrimaryBtn
                  variant="contained"
                  color="error"
                  onClick={() => {
                    clearFileInput();
                    setOpenModal(false);
                  }}
                >
                  Cancelar
                </PrimaryBtn>
                <PrimaryBtn variant="contained" onClick={handleSendFile} disabled={isSendingFile}>
                  {isSendingFile ? (
                    <CircularProgress size={24} sx={{ color: 'white' }} />
                  ) : (
                    <>
                      Enviar
                      <Send fontSize="small" sx={{ ml: 1 }} />
                    </>
                  )}
                </PrimaryBtn>
              </Box>
            </Box>
          )}
        </Box>
      </Modal>
      {openScheduleModal && (
        <Modal open onClose={() => setOpenScheduleModal(false)}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              backdropFilter: GLASS_BLUR,
              backgroundColor: GLASS_BACKGROUND,
              borderRadius: RADIUS_CARD,
              boxShadow: SHADOW,
              border: `1px solid ${alpha(NAVY, 0.06)}`,
              p: 4,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 2,
              }}
            >
              Programar mensaje
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                mb: 2,
              }}
            >
              <FormControl fullWidth>
                <InputLabel id="hour-select-label">Hora</InputLabel>
                <Select
                  labelId="hour-select-label"
                  value={selectedHour}
                  label="Hora"
                  onChange={(e) => setSelectedHour(e.target.value as number)}
                >
                  {availableHours.map((hour) => (
                    <MenuItem key={hour.value} value={hour.value} divider={hour.value === 23}>
                      {hour.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="minute-select-label">Minutos</InputLabel>
                <Select
                  labelId="minute-select-label"
                  value={selectedMinute}
                  label="Minutos"
                  onChange={(e) => setSelectedMinute(e.target.value as number)}
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <MenuItem key={i * 5} value={i * 5}>
                      {i * 5}m
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {scheduleError && (
              <Typography
                color="error"
                sx={{
                  mb: 2,
                }}
              >
                {scheduleError}
              </Typography>
            )}
            <Box
              sx={{
                mb: 2,
                p: 2,
                bgcolor: SURFACE_CONTAINER_LOW,
                borderRadius: RADIUS_CARD,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                El mensaje se enviará a las:
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 'bold',
                }}
              >
                {selectedHour}:{selectedMinute.toString().padStart(2, '0')} hrs
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mt: 1,
                }}
              >
                Mensaje:
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 'bold',
                }}
              >
                {messageToSend}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
              }}
            >
              <Button variant="outlined" onClick={() => setOpenScheduleModal(false)}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleScheduleMessage}
                disabled={selectedHour === '' || selectedMinute === '' || isSchedulingMessage}
              >
                {isSchedulingMessage ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Programar'}
              </Button>
            </Box>
          </Box>
        </Modal>
      )}
      <Box
        ref={footerRef}
        sx={{
          display: 'flex',
          width: '100%',
          p: 1,
          pt: 0.5,
          backgroundColor: 'transparent',
          gap: 1,
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          accept=".jpg,.jpeg,.png,.mp4,.pdf"
          onChange={(e) => {
            if (e.target.files) {
              setFileToSend(e.target.files[0]);
            }
          }}
        />
        <TextField
          id="outlined-basic-email"
          fullWidth
          multiline
          maxRows={10}
          onPaste={handlePaste}
          sx={{
            backgroundColor: SURFACE_CONTAINER_LOWEST,
            borderRadius: RADIUS_COIN,
            boxShadow: SHADOW,
            '& .MuiOutlinedInput-root': {
              borderRadius: RADIUS_COIN,
              padding: '10px',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .MuiInputBase-input': {
              caretColor: alpha(NAVY, 0.4),
              color: NAVY,
            },
          }}
          placeholder={placeholderMessage}
          value={messageToSend}
          onChange={(e) => {
            const newValue = e.target.value;
            setMessage(newValue);
            handleTyping(newValue);
          }}
          disabled={isSendingMessage || isRecordingAudio}
          inputRef={messageInputRef}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <QuickReplies
                    message={messageToSend}
                    onMessageChange={setMessage}
                    inputRef={messageInputRef}
                    disabled={isSendingMessage || isRecordingAudio}
                    onAttachClick={() => fileInputRef.current?.click()}
                    onSendFile={(qr) => setBucketFileToSend({ ...qr })}
                  />
                  <IconButton
                    size="small"
                    tabIndex={-1}
                    onClick={(e) => setEmojiAnchorEl(e.currentTarget)}
                    sx={{ color: alpha(NAVY, 0.55) }}
                    disabled={isSendingMessage || isRecordingAudio}
                  >
                    <InsertEmoticonIcon />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {messageToSend.length === 0 ? (
                    <VoiceRecorder
                      clientId={client.id}
                      anchorRef={footerRef}
                      disabled={isSendingMessage}
                      onActiveChange={setIsRecordingAudio}
                      onSent={(message) =>
                        setConversation((prev) => [...(prev ?? []), { ...message, status: 'sent' as MessageStatus }])
                      }
                    />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                      <IconButton
                        title="Programar mensaje"
                        size="small"
                        tabIndex={-1}
                        onClick={() => setOpenScheduleModal(true)}
                        disabled={isSendingMessage || client.chatType === 'pusher'}
                        sx={{ color: alpha(NAVY, 0.55) }}
                      >
                        <Schedule fontSize="small" />
                      </IconButton>
                      <IconButton
                        title="Enviar mensaje"
                        onClick={handleSendMessage}
                        disabled={isSendingMessage}
                        sx={{
                          backgroundColor: YELLOW,
                          color: NAVY,
                          width: '38px',
                          height: '38px',
                          '&:hover': { backgroundColor: YELLOW, opacity: 0.92 },
                          '&:disabled': { backgroundColor: alpha(YELLOW, 0.4), color: alpha(NAVY, 0.4) },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {isSendingMessage ? <CircularProgress size={20} /> : <Send fontSize="small" />}
                      </IconButton>
                    </Box>
                  )}
                </InputAdornment>
              ),
            },
          }}
        />
        <Popover
          open={Boolean(emojiAnchorEl)}
          anchorEl={emojiAnchorEl}
          onClose={() => setEmojiAnchorEl(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                mt: -2,
                p: 1,
                borderRadius: RADIUS_CARD,
                boxShadow: SHADOW_CARD,
              },
            },
          }}
        >
          {COMMON_EMOJIS.map((row) => (
            <Grid container key={`${row[0]}-${row[1]}`}>
              {row.map((emoji) => (
                <IconButton
                  key={emoji}
                  size="small"
                  onClick={() => handleEmojiClick(emoji)}
                  sx={{ fontSize: '1.5rem' }}
                >
                  {emoji}
                </IconButton>
              ))}
            </Grid>
          ))}
        </Popover>
      </Box>
    </>
  );
};

export default ChatFooter;
